import React, { createContext, useContext } from "react";
import { Toaster, toast } from "react-hot-toast";
import { cn } from "@assessment/utils/cn";
import GetHeroIcon from "@assessment/utils/getHeroIcons";
import { getMessageFromCode } from "@lxp-app/utils/messageCodeMapper";

type ShowErrorsFn = (
  title: string,
  errorCodes: string[],
  severity: "error" | "warning" | "success" | "info" | "event-change",
  description?: string
) => void;

const ToastContext = createContext<ShowErrorsFn | null>(null);

export const useShowErrors = () => {
  const context = useContext(ToastContext);
  return context;
};

export const ToasterWrapper = ({ children }: any) => {
  const showErrors: ShowErrorsFn = (
    title,
    errorCodes,
    severity,
    description
  ) => {
    const messages = errorCodes
      .map((code) => getMessageFromCode(code?.split("_")[0] || code))
      .filter(Boolean);

    toast.custom(
      <div
        className={cn(
          "w-[26rem] max-w-[35rem] px-4 py-3 border shadow-sm rounded-lg",
          severity === "success" && "bg-[#f0fdf4]",
          severity === "error" && "bg-[#fef2f2]",
          severity === "warning" && "bg-[#fefce8]",
          severity === "info" && "bg-[#eff6ff]",
          severity === "event-change" && "bg-[#ffffff]"
        )}
      >
        <div
          className={cn(
            "flex",
            severity === "success" && "text-green-800",
            severity === "error" && "text-red-800",
            severity === "warning" && "text-yellow-800",
            severity === "info" && "text-blue-800",
            severity === "event-change" && "text-black-800"
          )}
        >
          <div className="mr-2">
            {severity === "success" && (
              <GetHeroIcon
                variant="solid"
                name="CheckCircleIcon"
                className="w-5 h-5 text-green-400"
              />
            )}
            {severity === "error" && (
              <GetHeroIcon
                variant="solid"
                name="XCircleIcon"
                className="w-5 h-5 text-red-400"
              />
            )}
            {severity === "info" && (
              <GetHeroIcon
                variant="solid"
                name="InformationCircleIcon"
                className="w-5 h-5 text-blue-400"
              />
            )}
            {severity === "warning" && (
              <GetHeroIcon
                variant="solid"
                name="ExclamationTriangleIcon"
                className="w-5 h-5 text-yellow-400"
              />
            )}
          </div>
          <div>
            <label className="text-sm font-medium capitalize">{title}</label>
            {description && <p className="text-sm">{description}</p>}
            <ul className="w-full mt-2 mb-3 space-y-1 list-disc list-inside">
              {messages.map((msg, id) => (
                <li
                  key={id}
                  className={cn(
                    "text-sm leading-tight break-words",
                    severity === "success" && "text-green-800",
                    severity === "error" && "text-red-800",
                    severity === "info" && "text-blue-800",
                    severity === "warning" && "text-yellow-800",
                    severity === "event-change" && "text-black-800"
                  )}
                >
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>,
      { duration: 5000, position: "bottom-right" }
    );
  };
  return (
    <ToastContext.Provider value={showErrors}>
      <Toaster position="bottom-right" reverseOrder={false} /> {children}
    </ToastContext.Provider>
  );
};
